import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Custom Orders', href: '/custom-orders' },
  { label: 'Gallery', href: '/#gallery' },
  { label: 'Classes', href: '/#classes' },
  { label: 'Contact', href: '/#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const handleHashLink = (href: string) => {
    if (href.startsWith('/#')) {
      const id = href.replace('/#', '');
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-cream-50 shadow-sm' : 'bg-transparent'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 md:px-8 h-14 md:h-20 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-3 cursor-pointer">
          <img
            src="https://static.readdy.ai/image/b56ddf5676badcd01c30e06215815ec0/bb1c6698515d81ebf3fbedc388c9a65a.png"
            alt="Sweet Celebrations Logo"
            className="h-9 md:h-14 w-auto object-contain"
          />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) =>
            link.href.startsWith('/#') ? (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleHashLink(link.href);
                }}
                className={`text-sm font-sans font-semibold tracking-wide cursor-pointer transition-colors duration-200 whitespace-nowrap ${
                  scrolled
                    ? 'text-charcoal-800 hover:text-blush-500'
                    : 'text-white hover:text-blush-200'
                }`}
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-sans font-semibold tracking-wide cursor-pointer transition-colors duration-200 whitespace-nowrap ${
                  scrolled
                    ? 'text-charcoal-800 hover:text-blush-500'
                    : 'text-white hover:text-blush-200'
                }`}
              >
                {link.label}
              </Link>
            )
          )}
          <Link
            to="/custom-orders"
            className="ml-2 bg-blush-500 text-white px-5 py-2 rounded-full text-sm font-semibold whitespace-nowrap hover:bg-blush-600 transition-colors duration-200 cursor-pointer"
          >
            Order Now
          </Link>
        </div>

        {/* Mobile Hamburger */}
        <button
          className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
          onClick={() => setMobileOpen(!mobileOpen)}
          aria-label="Toggle menu"
        >
          <i
            className={`text-2xl ${scrolled ? 'text-charcoal-800' : 'text-white'} ${
              mobileOpen ? 'ri-close-line' : 'ri-menu-3-line'
            }`}
          />
        </button>
      </nav>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-cream-50 border-t border-warm-200 px-4 py-3 flex flex-col gap-2">
          {navLinks.map((link) =>
            link.href.startsWith('/#') ? (
              <a
                key={link.label}
                href={link.href}
                onClick={(e) => {
                  e.preventDefault();
                  handleHashLink(link.href);
                  setMobileOpen(false);
                }}
                className="text-charcoal-800 font-semibold text-sm py-1.5 border-b border-warm-200 cursor-pointer"
              >
                {link.label}
              </a>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className="text-charcoal-800 font-semibold text-sm py-1.5 border-b border-warm-200 cursor-pointer"
              >
                {link.label}
              </Link>
            )
          )}
          <Link
            to="/custom-orders"
            className="mt-1 bg-blush-500 text-white px-5 py-2 rounded-full text-sm font-semibold text-center whitespace-nowrap hover:bg-blush-600 transition-colors duration-200 cursor-pointer"
          >
            Order Now
          </Link>
        </div>
      )}
    </header>
  );
}
